import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Calendar } from '../ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Checkbox } from '../ui/checkbox';
import { ScrollArea } from '../ui/scroll-area';
import { toast } from 'sonner';
import { Calendar as CalendarIcon, Loader2, Plus, Pencil, ChevronDown, X } from 'lucide-react';
import { format } from 'date-fns';
import { STANDARD_INDUSTRIES, INDUSTRY_CATEGORIES, getIndustryLabel } from '../../constants/industries';
import { planGateFromResponse } from '../../lib/planGate';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const countries = [
  { code: 'SA', en: 'Saudi Arabia', ar: 'السعودية' },
  { code: 'AE', en: 'UAE', ar: 'الإمارات' },
  { code: 'OM', en: 'Oman', ar: 'عمان' },
  { code: 'KW', en: 'Kuwait', ar: 'الكويت' },
  { code: 'BH', en: 'Bahrain', ar: 'البحرين' },
  { code: 'QA', en: 'Qatar', ar: 'قطر' }
];

export function AdminNewsDialog({ open, onOpenChange, news, token, language, onSuccess, onPlanGate }) {
  const isEdit = !!news;
  const isRTL = language === 'ar';
  
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    title_en: '',
    content_text: '',
    summary_ai: '',
    summary_en: '',
    country: 'SA',
    source: 'Manual Entry',
    url: '',
    language: 'en',
    image_url: '',
    industries: [],
    published_at: new Date()
  });
  
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showIndustryPicker, setShowIndustryPicker] = useState(false);

  useEffect(() => {
    if (news) {
      // Convert legacy sector to industries array if needed
      let industries = news.industries || [];
      if ((!industries || industries.length === 0) && news.sector) {
        industries = [news.sector];
      }
      
      setFormData({
        title: news.title || '',
        title_en: news.title_en || '',
        content_text: news.content_text || '',
        summary_ai: news.summary_ai || '',
        summary_en: news.summary_en || '',
        country: news.country || 'SA',
        source: news.source || 'Manual Entry',
        url: news.url || '',
        language: news.language || 'en',
        image_url: news.image_url || '',
        industries: industries,
        published_at: news.published_at ? new Date(news.published_at) : new Date()
      });
    } else {
      setFormData({
        title: '',
        title_en: '',
        content_text: '',
        summary_ai: '',
        summary_en: '',
        country: 'SA',
        source: 'Manual Entry',
        url: '',
        language: 'en',
        image_url: '',
        industries: [],
        published_at: new Date()
      });
    }
  }, [news, open]);

  // Helper to toggle a single industry
  const toggleIndustry = (industryId) => {
    setFormData(prev => {
      const current = prev.industries || [];
      return current.includes(industryId)
        ? { ...prev, industries: current.filter(id => id !== industryId) }
        : { ...prev, industries: [...current, industryId] };
    });
  };

  // Remove an industry from selection
  const removeIndustry = (industryId) => {
    setFormData(prev => ({
      ...prev,
      industries: (prev.industries || []).filter(id => id !== industryId)
    }));
  };

  // Toggle all industries in a category group
  const toggleCategory = (categoryId) => {
    const categoryIndustries = STANDARD_INDUSTRIES.filter(ind => ind.category === categoryId).map(ind => ind.id);
    setFormData(prev => {
      const current = prev.industries || [];
      const allSelected = categoryIndustries.every(id => current.includes(id));
      if (allSelected) {
        return { ...prev, industries: current.filter(id => !categoryIndustries.includes(id)) };
      } else {
        const newSet = new Set([...current, ...categoryIndustries]);
        return { ...prev, industries: Array.from(newSet) };
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title_en.trim()) {
      toast.error(language === 'ar' ? 'العنوان مطلوب' : 'Title is required');
      return;
    }
    
    setLoading(true);
    
    try {
      const url = isEdit 
        ? `${BACKEND_URL}/api/admin/news/${news._id}`
        : `${BACKEND_URL}/api/admin/news`;
      
      const method = isEdit ? 'PUT' : 'POST';
      
      const payload = {
        ...formData,
        // BUG-03: backend requires a non-empty `title` and `content_text`.
        // The form only collects `title_en` (Title field) and `summary_en`
        // (Summary field), so map them onto the required backend fields.
        title: formData.title_en,
        content_text: formData.content_text || formData.summary_en || formData.title_en,
        published_at: formData.published_at.toISOString()
      };
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });
      
      if (!response.ok) {
        const pg = await planGateFromResponse(response);
        if (pg && onPlanGate) { onOpenChange(false); onPlanGate(pg); return; }
        const error = await response.json().catch(() => ({}));
        throw new Error(error.detail || 'Failed to save news');
      }
      
      toast.success(isEdit 
        ? (language === 'ar' ? 'تم تحديث الخبر بنجاح' : 'News updated successfully')
        : (language === 'ar' ? 'تم إنشاء الخبر بنجاح' : 'News created successfully')
      );
      
      onSuccess?.();
      onOpenChange(false);
      
    } catch (error) {
      console.error('Error saving news:', error);
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const txt = {
    // Dialog title/subtitle keep bilingual for UX
    title: language === 'ar' ? (isEdit ? 'تعديل الخبر' : 'إنشاء خبر جديد') : (isEdit ? 'Edit News' : 'Create News'),
    subtitle: language === 'ar' ? 'أدخل تفاصيل الخبر' : 'Enter news details',
    // All form labels are ALWAYS in English
    titleLabel: 'Title',
    summary: 'Summary',
    country: 'Country',
    source: 'Source',
    url: 'URL',
    imageUrl: 'Image URL',
    industries: 'Industries',
    publishDate: 'Publish Date',
    save: 'Save',
    cancel: 'Cancel',
    saving: 'Saving...'
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isEdit ? <Pencil className="w-5 h-5" /> : <Plus className="w-5 h-5" />}
            {txt.title}
          </DialogTitle>
          <DialogDescription>{txt.subtitle}</DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Title */}
          <div className="space-y-2">
            <Label>{txt.titleLabel} *</Label>
            <Input
              value={formData.title_en}
              onChange={(e) => setFormData(prev => ({ ...prev, title_en: e.target.value }))}
              placeholder="Enter news title..."
              dir="ltr"
              className="bg-background"
              data-testid="news-title-input"
            />
          </div>
          
          {/* Summary */}
          <div className="space-y-2">
            <Label>{txt.summary}</Label>
            <Textarea
              value={formData.summary_en}
              onChange={(e) => setFormData(prev => ({ ...prev, summary_en: e.target.value }))}
              placeholder="AI-generated summary..."
              rows={6}
              className="bg-background resize-y"
              data-testid="news-summary-input"
            />
          </div>
          
          {/* Country + Source Row */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>{txt.country}</Label>
              <Select
                value={formData.country}
                onValueChange={(value) => setFormData(prev => ({ ...prev, country: value }))}
              >
                <SelectTrigger className="bg-background" data-testid="news-country-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {countries.map(c => (
                    <SelectItem key={c.code} value={c.code}>{c.en}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{txt.source}</Label>
              <Input
                value={formData.source}
                onChange={(e) => setFormData(prev => ({ ...prev, source: e.target.value }))}
                placeholder="News source..."
                className="bg-background"
                data-testid="news-source-input"
              />
            </div>
          </div>
          
          {/* Industries Multi-Select */}
          <div className="space-y-2">
            <Label>{txt.industries}</Label>
            <Popover open={showIndustryPicker} onOpenChange={setShowIndustryPicker}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className="w-full justify-between text-left font-normal h-auto min-h-10 bg-background"
                  data-testid="news-industries-trigger"
                >
                  <div className="flex flex-wrap gap-1 flex-1">
                    {formData.industries && formData.industries.length > 0 ? (
                      formData.industries.map(id => (
                        <span
                          key={id}
                          className="inline-flex items-center gap-1 px-2 py-0.5 bg-primary/10 text-primary rounded text-xs"
                        >
                          {getIndustryLabel(id, 'en')}
                          <X
                            className="w-3 h-3 cursor-pointer hover:text-destructive"
                            onClick={(e) => { e.stopPropagation(); removeIndustry(id); }}
                          />
                        </span>
                      ))
                    ) : (
                      <span className="text-muted-foreground">Select industries...</span>
                    )}
                  </div>
                  <ChevronDown className="w-4 h-4 opacity-50 shrink-0 ml-2" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[440px] p-0" align="start">
                <ScrollArea className="h-[320px]">
                  <div className="p-2">
                    {INDUSTRY_CATEGORIES.map(category => {
                      const categoryIndustries = STANDARD_INDUSTRIES.filter(ind => ind.category === category.id);
                      if (categoryIndustries.length === 0) return null;
                      const selected = formData.industries || [];
                      const selectedInCategory = categoryIndustries.filter(ind => selected.includes(ind.id));
                      const allSelected = selectedInCategory.length === categoryIndustries.length;
                      const someSelected = selectedInCategory.length > 0 && !allSelected;
                      return (
                        <div key={category.id} className="mb-3">
                          {/* Category header with checkbox */}
                          <div
                            className="flex items-center gap-2 px-2 py-1.5 bg-muted/50 rounded cursor-pointer hover:bg-muted/70"
                            onClick={() => toggleCategory(category.id)}
                            data-testid={`category-checkbox-${category.id}`}
                          >
                            <Checkbox
                              checked={allSelected}
                              data-state={someSelected ? 'indeterminate' : allSelected ? 'checked' : 'unchecked'}
                              className={someSelected ? 'opacity-60' : ''}
                              onCheckedChange={() => toggleCategory(category.id)}
                              onClick={(e) => e.stopPropagation()}
                            />
                            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                              {category.en}
                            </span>
                            {selectedInCategory.length > 0 && (
                              <span className="ml-auto text-xs text-primary font-medium">
                                {selectedInCategory.length}/{categoryIndustries.length}
                              </span>
                            )}
                          </div>
                          {/* Individual industry checkboxes */}
                          <div className="mt-1 space-y-0.5 pl-1">
                            {categoryIndustries.map(industry => (
                              <div
                                key={industry.id}
                                className="flex items-center gap-2 px-2 py-1.5 hover:bg-muted/40 rounded cursor-pointer"
                                onClick={() => toggleIndustry(industry.id)}
                              >
                                <Checkbox
                                  checked={selected.includes(industry.id)}
                                  onCheckedChange={() => toggleIndustry(industry.id)}
                                  onClick={(e) => e.stopPropagation()}
                                  data-testid={`industry-checkbox-${industry.id}`}
                                />
                                <span className="text-sm">{industry.en}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </ScrollArea>
              </PopoverContent>
            </Popover>
          </div>
          
          {/* URL */}
          <div className="space-y-2">
            <Label>{txt.url}</Label>
            <Input
              value={formData.url}
              onChange={(e) => setFormData(prev => ({ ...prev, url: e.target.value }))}
              placeholder="https://..."
              dir="ltr"
              className="bg-background"
              data-testid="news-url-input"
            />
          </div>
          
          {/* Image URL */}
          <div className="space-y-2">
            <Label>{txt.imageUrl}</Label>
            <Input
              value={formData.image_url}
              onChange={(e) => setFormData(prev => ({ ...prev, image_url: e.target.value }))}
              placeholder="https://..."
              dir="ltr"
              className="bg-background"
              data-testid="news-image-url-input"
            />
            {formData.image_url && (
              <div className="mt-2 rounded-lg overflow-hidden border bg-muted/20 max-w-[200px]">
                <img
                  src={formData.image_url}
                  alt="Preview"
                  className="w-full h-auto object-cover"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              </div>
            )}
          </div>
          
          {/* Publish Date */}
          <div className="space-y-2">
            <Label>{txt.publishDate}</Label>
            <Popover open={showDatePicker} onOpenChange={setShowDatePicker}>
              <PopoverTrigger asChild>
                <Button variant="outline" className="w-full justify-start text-left font-normal bg-background">
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {formData.published_at ? format(formData.published_at, 'PPP') : 'Select date'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={formData.published_at}
                  onSelect={(date) => {
                    setFormData(prev => ({ ...prev, published_at: date || new Date() }));
                    setShowDatePicker(false);
                  }}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>
          
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={loading} data-testid="news-dialog-cancel">
              {txt.cancel}
            </Button>
            <Button type="submit" disabled={loading} data-testid="news-dialog-save">
              {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {loading ? txt.saving : txt.save}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default AdminNewsDialog;
